import { Component, input } from '@angular/core';

/**
 * Loader de M31: el cuadro de la marca "respirando", con anillos que se expanden como una señal
 * y tres puntos que laten debajo. Es el mismo lenguaje de movimiento que el chat de MU.
 *
 * Ocupa todo el alto disponible y centra su contenido, así que puede usarse como pantalla de
 * carga. No aparece hasta pasados 120 ms: si lo que se espera llega antes, el usuario nunca ve
 * un destello de loader.
 */
@Component({
  selector: 'app-m31-loader',
  standalone: true,
  template: `
    <div
      class="loader-pantalla bg-white dark:bg-black"
      role="status"
      aria-live="polite"
      [attr.aria-label]="mensaje()"
    >
      <div class="loader-contenido">
        <div class="marca">
          <span class="anillo"></span>
          <span class="anillo anillo-2"></span>
          <div class="cuadro">
            <span class="texto-marca">M31</span>
          </div>
        </div>

        <div class="puntos" aria-hidden="true">
          <i></i><i></i><i></i>
        </div>

        <p class="mensaje text-gray-500 dark:text-gray-400">{{ mensaje() }}</p>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }

    .loader-pantalla {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* El contenido entra con retraso: una espera corta no llega a mostrar nada. */
    .loader-contenido {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1.5rem;
      opacity: 0;
      animation: entrada 360ms cubic-bezier(0.32, 0.72, 0, 1) 120ms forwards;
    }

    .marca {
      position: relative;
      width: 76px;
      height: 76px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .cuadro {
      position: relative;
      z-index: 1;
      width: 76px;
      height: 76px;
      border-radius: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #0a9d65 0%, #14b683 100%);
      box-shadow:
        inset 0 1px 0 rgb(255 255 255 / 0.28),
        0 14px 30px -12px rgb(20 182 131 / 0.7);
      animation: respirar 2.4s ease-in-out infinite;
    }

    .texto-marca {
      color: #fff;
      font-size: 1.6rem;
      font-weight: 700;
      letter-spacing: -0.06em;
    }

    /* Anillos que se expanden desde el cuadro, como la "señal" de MU. */
    .anillo {
      position: absolute;
      inset: 0;
      border-radius: 22px;
      border: 2px solid rgb(20 182 131 / 0.5);
      animation: senal 2.4s cubic-bezier(0.2, 0.6, 0.3, 1) infinite;
    }

    .anillo-2 { animation-delay: 1.2s; }

    /* Tres puntos que laten en cascada, como el "escribiendo…" de MU. */
    .puntos { display: flex; gap: 7px; }

    .puntos i {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: #14b683;
      opacity: 0.34;
      animation: latir 1.2s ease-in-out infinite;
    }

    .puntos i:nth-child(2) { animation-delay: 0.16s; }
    .puntos i:nth-child(3) { animation-delay: 0.32s; }

    .mensaje {
      margin: 0;
      font-size: 0.8125rem;
      font-weight: 500;
    }

    @keyframes entrada {
      from { opacity: 0; transform: translateY(8px) scale(0.98); }
      to   { opacity: 1; transform: none; }
    }

    @keyframes respirar {
      0%, 100% { transform: scale(1); }
      50%      { transform: scale(1.045); }
    }

    @keyframes senal {
      0%   { transform: scale(1);   opacity: 0.7; }
      80%, 100% { transform: scale(1.85); opacity: 0; }
    }

    @keyframes latir {
      0%, 72%, 100% { opacity: 0.34; transform: translateY(0); }
      36%           { opacity: 1;    transform: translateY(-4px); }
    }

    /* Con movimiento reducido no hay anillos ni saltos: solo un latido suave de opacidad. */
    @media (prefers-reduced-motion: reduce) {
      .cuadro, .anillo { animation: none; }
      .anillo { display: none; }
      .puntos i { animation: latir-suave 1.6s ease-in-out infinite; }
      .loader-contenido { animation: entrada-suave 200ms ease 120ms forwards; }

      @keyframes latir-suave { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
      @keyframes entrada-suave { from { opacity: 0; } to { opacity: 1; } }
    }
  `]
})
export class M31LoaderComponent {
  readonly mensaje = input<string>('Preparando tu espacio…');
}
